import type { Boost, SearchDescriptor } from '../descriptor';
import type { Predicate } from '../predicate';
import { and, eqOpt, inList } from '../predicate';
import {
  eqStringLiteralWhenPresent,
  eqStringLiteralWhenTruthy,
  eqWhenPresent,
  eqWhenTruthy,
  groupedEqStringLiteralsWhenPresent,
} from './scopeFilters';

interface LotOrderReference {
  readonly purchaseOrderId?: number;
  readonly salesOrderId?: number;
  readonly buySellOrderId?: number;
  readonly slug: string;
  readonly slugs?: readonly string[];
}

export interface LotPalletTagReference {
  readonly palletTagId: number;
  readonly tagNumber: string;
  // Absent on Algolia lot records: jobs/src/functions/inventory/lot.algolia.ts omits it for record size.
  readonly tagNumbers?: readonly string[];
}

export type LotSearchStatus = 'DRAFT' | 'MID_RECEIVING' | 'OPEN' | 'MID_PRODUCTION_RUN' | 'LOCKED' | 'CLOSED';

/**
 * Canonical shape of an indexed lot.
 */
export interface LotDoc {
  readonly objectID: string;
  readonly lotId: number;
  readonly organizationId: number;
  readonly productId: number;
  readonly warehouseId: number;
  readonly slug: string;
  readonly slugs?: readonly string[];
  readonly description: string;
  readonly status: LotSearchStatus;
  readonly commodity: string;
  readonly commodityStyle: string;
  readonly commodityLabel: string;
  readonly commoditySize: string;
  readonly commodityUnit: string;
  readonly commodityGrade?: string;
  readonly countryOfOrigin: string;
  readonly organic: boolean;
  readonly quantity: number;
  readonly lockReason?: string;
  readonly receiveDateTimestamp: number;
  readonly receiveDate: string;
  readonly receivedByWeight: boolean;
  readonly weight: number;
  readonly reserved: number;
  readonly quantityAvailable?: number;
  readonly vendor: string;
  readonly grower: string;
  readonly palletTags: readonly LotPalletTagReference[];
  readonly grade: string;
  // Only repacked lots carry this, so it is absent on most documents.
  readonly repackDate?: string | null;
  readonly orders: readonly LotOrderReference[];
}

export const LOT_DESCRIPTOR: SearchDescriptor<LotDoc> = {
  entity: 'lot',
  indexName: { algolia: 'lots', typesense: 'lots' },
  searchable: [
    'slug',
    'slugs',
    'orders',
    'description',
    'status',
    'commodity',
    'organic',
    'countryOfOrigin',
    'palletTags',
    'warehouseId',
  ],
  filterable: [
    'commodity',
    'commodityLabel',
    'commoditySize',
    'commodityStyle',
    'commodityUnit',
    'countryOfOrigin',
    'grade',
    'organic',
    'organizationId',
    'slug',
    'status',
    'warehouseId',
    'commodityGrade',
  ],
  defaults: { hitsPerPage: 20 },
  algolia: {
    searchableAttributes: [
      { path: ['slug'] },
      { path: ['slugs'] },
      { path: ['orders', 'slug'] },
      { path: ['orders', 'slugs'] },
      { path: ['description'] },
      { path: ['status'] },
      { path: ['commodity'], mode: 'unordered' },
      { path: ['organic'], mode: 'unordered' },
      { path: ['countryOfOrigin'], mode: 'unordered' },
      { path: ['palletTags'], mode: 'unordered' },
      { path: ['warehouseId'], mode: 'unordered' },
    ],
    attributesForFaceting: [
      { path: ['commodity'] },
      { path: ['commodityLabel'], mode: 'filterOnly' },
      { path: ['commoditySize'], mode: 'filterOnly' },
      { path: ['commodityStyle'], mode: 'filterOnly' },
      { path: ['commodityUnit'], mode: 'filterOnly' },
      { path: ['countryOfOrigin'] },
      { path: ['grade'], mode: 'filterOnly' },
      { path: ['organic'] },
      { path: ['organizationId'], mode: 'filterOnly' },
      { path: ['slug'], mode: 'searchable' },
      { path: ['status'], mode: 'searchable' },
      { path: ['warehouseId'] },
    ],
  },
  ranking: {
    algolia: { customRanking: [{ path: ['receiveDateTimestamp'], direction: 'desc' }] },
    typesense: {
      queryBy:
        'lotNumber,slugTokens,orderSlugTokens,palletTagNumbers,description,status,commodity,countryOfOrigin,lotProductsText,notesText',
      // Relevance first: a sort_by without _text_match orders by that field alone and an exact lot number can fall off the page.
      sortBy: '_text_match:desc,receiveDateTimestamp:desc',
    },
  },
  tenantField: 'organizationId',
};

export interface LotSimilarityValues {
  readonly commodityStyle?: string | null;
  readonly commodityLabel?: string | null;
  readonly commoditySize?: string | null;
  readonly commodityUnit?: string | null;
  readonly organic?: boolean | null;
  readonly commodityGrade?: string | null;
}

export const LOT_SIMILARITY_BOOSTS = (values: LotSimilarityValues): readonly Boost<LotDoc>[] => [
  { field: 'commodityStyle', value: values.commodityStyle, weight: 5 },
  { field: 'commodityLabel', value: values.commodityLabel, weight: 3 },
  { field: 'commoditySize', value: values.commoditySize, weight: 3 },
  { field: 'commodityUnit', value: values.commodityUnit, weight: 2 },
  { field: 'organic', value: values.organic, weight: 2 },
  { field: 'commodityGrade', value: values.commodityGrade, weight: 1 },
];

interface LotScope {
  readonly warehouseId?: number | null;
  readonly commodity?: string | null;
  readonly organic?: boolean | null;
  readonly countryOfOrigin?: string | null;
}

const groupedStatuses = (statuses: readonly LotSearchStatus[]): Predicate<LotDoc> => ({
  kind: 'in',
  field: 'status',
  values: statuses,
});

export const LOT_PICKING_STATUSES = (midReceiving: boolean): readonly LotSearchStatus[] =>
  midReceiving ? ['OPEN', 'MID_PRODUCTION_RUN', 'MID_RECEIVING'] : ['OPEN', 'MID_PRODUCTION_RUN'];

export const LOT_SELECTABLE_FOR_PICKING = (scope: LotScope & { readonly midReceiving: boolean }): Predicate<LotDoc> =>
  and([
    eqOpt<LotDoc>('warehouseId', scope.warehouseId),
    inList<LotDoc>('status', LOT_PICKING_STATUSES(scope.midReceiving)),
    eqStringLiteralWhenTruthy<LotDoc>('commodity', scope.commodity),
    eqWhenTruthy<LotDoc>('organic', scope.organic),
    eqWhenTruthy<LotDoc>('countryOfOrigin', scope.countryOfOrigin),
  ]);

export const LOT_SELECTABLE_FOR_TRANSFER = (scope: LotScope): Predicate<LotDoc> =>
  and([
    eqOpt<LotDoc>('warehouseId', scope.warehouseId),
    inList<LotDoc>('status', ['OPEN', 'MID_PRODUCTION_RUN', 'MID_RECEIVING'] satisfies readonly LotSearchStatus[]),
    eqStringLiteralWhenTruthy<LotDoc>('commodity', scope.commodity),
    eqWhenTruthy<LotDoc>('countryOfOrigin', scope.countryOfOrigin),
  ]);

export const LOT_SELECTABLE_FOR_PICK_TICKET = (): Predicate<LotDoc> =>
  inList<LotDoc>('status', ['OPEN', 'MID_PRODUCTION_RUN', 'MID_RECEIVING'] satisfies readonly LotSearchStatus[]);

export const LOT_SELECTABLE_FOR_PRODUCTION_RUN = (
  scope: LotScope & { readonly commodityNames?: readonly string[] | null; readonly midReceiving: boolean }
): Predicate<LotDoc> =>
  and([
    eqStringLiteralWhenPresent<LotDoc>('commodity', scope.commodity),
    groupedEqStringLiteralsWhenPresent<LotDoc>('commodity', scope.commodityNames),
    eqWhenPresent<LotDoc>('countryOfOrigin', scope.countryOfOrigin),
    eqWhenPresent<LotDoc>('organic', scope.organic),
    eqOpt<LotDoc>('warehouseId', scope.warehouseId),
    groupedStatuses(scope.midReceiving ? ['OPEN', 'MID_RECEIVING'] : ['OPEN']),
  ]);
